import { useCallback, useEffect, useRef, useState } from 'react';

const LEAD_MS = 3000;
const MIN_S = 0.5, MAX_S = 1;

// Circle size at the end of each phase: inhale grows (first of a double inhale stops part-way), hold stays, exhale shrinks.
const endLevels = (phases) => {
  const out = []; let lvl = MIN_S;
  phases.forEach((p, i) => {
    if (p.type === 'inhale') lvl = phases[i + 1]?.type === 'inhale' ? MIN_S + (MAX_S - MIN_S) * 0.75 : MAX_S;
    else if (p.type === 'exhale') lvl = MIN_S;
    out.push(lvl);
  });
  return out;
};
const ease = (t) => (t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2);

/** Wall-clock driven breathing timer. Voice is a listener (onCue) and never controls timing. */
export function useBreathingTimer({ phases, totalCycles, circleRef, onCue, onFinish }) {
  const ms = phases.map(p => p.seconds * 1000);
  const cycleMs = ms.reduce((a, b) => a + b, 0);
  const levels = endLevels(phases);
  const st = useRef({ acc: 0, runAt: null, cycle: 0, idx: 0, phaseStart: 0, started: false, done: false, nextCued: false });
  const raf = useRef(null);
  const cb = useRef({ onCue, onFinish }); cb.current = { onCue, onFinish };
  const [view, setView] = useState({ lead: 3, idx: 0, cycle: 0, countdown: 0, elapsed: 0, remaining: Math.round(totalCycles * cycleMs / 1000), running: false, finished: false });

  const now = () => performance.now();
  const elapsed = () => { const s = st.current; return s.acc + (s.runAt != null ? now() - s.runAt : 0); };

  const frame = useCallback(() => {
    const s = st.current; const e = elapsed() - LEAD_MS;
    if (e < 0) {
      setView(v => ({ ...v, lead: Math.ceil(-e / 1000) }));
      if (circleRef.current) circleRef.current.style.transform = `scale(${MIN_S})`;
      raf.current = requestAnimationFrame(frame); return;
    }
    if (!s.started) { s.started = true; cb.current.onCue?.(phases[0], 0, 'phase'); }
    while (e >= s.phaseStart + ms[s.idx]) {
      s.phaseStart += ms[s.idx]; s.idx += 1; s.nextCued = false;
      if (s.idx >= phases.length) { s.idx = 0; s.cycle += 1; }
      if (s.cycle >= totalCycles) { finish(); return; }
      cb.current.onCue?.(phases[s.idx], s.cycle, 'phase');
    }
    const dur = ms[s.idx]; const into = e - s.phaseStart;
    const lastPhase = s.idx === phases.length - 1 && s.cycle < totalCycles - 1;
    if (lastPhase && !s.nextCued && dur >= 4000 && dur - into <= 2000) { s.nextCued = true; cb.current.onCue?.(null, s.cycle, 'next'); }
    const from = levels[(s.idx - 1 + phases.length) % phases.length]; const to = levels[s.idx];
    if (circleRef.current) circleRef.current.style.transform = `scale(${from + (to - from) * ease(Math.min(1, into / dur))})`;
    const leftInCycle = ms.slice(s.idx).reduce((a, b) => a + b, 0) - into;
    const remaining = (leftInCycle + (totalCycles - s.cycle - 1) * cycleMs) / 1000;
    setView(v => {
      const nv = { ...v, lead: 0, idx: s.idx, cycle: s.cycle, countdown: Math.ceil((dur - into) / 1000), elapsed: Math.floor(e / 1000), remaining: Math.ceil(remaining) };
      return (nv.idx === v.idx && nv.cycle === v.cycle && nv.countdown === v.countdown && nv.elapsed === v.elapsed && nv.remaining === v.remaining && nv.lead === v.lead) ? v : nv;
    });
    raf.current = requestAnimationFrame(frame);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phases, totalCycles]);

  const stopLoop = () => { if (raf.current) cancelAnimationFrame(raf.current); raf.current = null; };

  const finish = (early = false) => {
    const s = st.current; if (s.done) return;
    const e = Math.max(0, elapsed() - LEAD_MS);
    s.acc = elapsed(); s.runAt = null; s.done = true; stopLoop();
    const cycles = early ? s.cycle : totalCycles;
    setView(v => ({ ...v, running: false, finished: true, elapsed: Math.round(e / 1000) }));
    cb.current.onFinish?.({ seconds: Math.round(e / 1000), cycles, early });
  };

  const start = () => { const s = st.current; if (s.done || s.runAt != null) return; s.runAt = now(); setView(v => ({ ...v, running: true })); stopLoop(); raf.current = requestAnimationFrame(frame); };
  const pause = () => { const s = st.current; if (s.runAt == null) return; s.acc = elapsed(); s.runAt = null; stopLoop(); setView(v => ({ ...v, running: false })); };
  const skipHold = () => {
    const s = st.current; if (phases[s.idx]?.type !== 'hold' || s.runAt == null) return;
    const e = elapsed() - LEAD_MS; if (e < 0) return;
    s.phaseStart = e - ms[s.idx]; // hold ends now; real elapsed time and later phases are unchanged
  };

  useEffect(() => () => stopLoop(), []);
  return { view, start, pause, resume: start, end: () => finish(true), skipHold, cycleSec: cycleMs / 1000 };
}
